/**
 * The switches and choices a module declares in its manifest (admin.controls),
 * rendered generically: "live" ones at the top of the module's phase in Työpaja,
 * "prepare" ones in the flow builder and under the live ones in Työpaja.
 * Each control reads and writes an existing instance key through
 * useInstanceRecord, so the same switch shows the same state everywhere.
 * A control with lockOnceReached is read-only once the live workshop has
 * reached the module's step (`reached`).
 * specs/admin-instance-lifecycle.md → "Module decisions come from module config".
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Toggle from '../ui/Toggle';
import useInstanceRecord, { readPath } from '../../hooks/useInstanceRecord';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: ${p => (p.$compact ? '8px 0' : '0 0 16px')};
  padding: ${p => (p.$compact ? '0' : '6px 14px')};
  border-radius: 10px;
  background: ${p => (p.$compact ? 'transparent' : 'rgba(37, 99, 235, 0.07)')};
  border: ${p => (p.$compact ? 'none' : '1px solid rgba(37, 99, 235, 0.25)')};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
`;

const Text = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #eee;

  small {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const NumberInput = styled.input`
  width: 64px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #111;
  color: #fff;
  font-size: 14px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #f87171;
`;

const Options = styled.fieldset`
  margin: 0 0 8px;
  padding: 0;
  border: none;
  display: flex;
  flex-direction: column;
  gap: 6px;

  label { display: flex; gap: 8px; align-items: flex-start; font-size: 14px; color: #eee; cursor: pointer; }
  input { margin-top: 3px; }
  small { display: block; font-size: 12px; color: rgba(255, 255, 255, 0.5); }
`;

function text(labels, language) {
  return labels?.[language] || labels?.fi || '';
}

/** A choice: one of its options, as radio buttons (validateAdminControls.js). */
const Locked = styled.p`
  margin: -4px 0 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
`;

function ChoiceRow({ control, value, disabled, onSave }) {
  const { language } = useTranslation();
  const name = `${control.store}.${control.key}`;
  return (
    <div>
      <Row>
        <Text>
          {text(control.labels, language)}
          {control.hints && <small>{text(control.hints, language)}</small>}
        </Text>
      </Row>
      <Options aria-label={text(control.labels, language)}>
        {control.options.map((o) => (
          <label key={o.value}>
            <input type="radio" name={name} value={o.value} checked={value === o.value} disabled={disabled}
              onChange={() => onSave(o.value)} />
            <span>
              {text(o.labels, language)}
              {o.hints && <small>{text(o.hints, language)}</small>}
            </span>
          </label>
        ))}
      </Options>
    </div>
  );
}

function ControlRow({ control, value, disabled, onSave }) {
  const { language } = useTranslation();
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const saveNumber = () => {
    const parsed = Number.parseInt(draft, 10);
    if (Number.isNaN(parsed)) { setDraft(value); return; }
    const clamped = Math.min(control.max ?? parsed, Math.max(control.min ?? parsed, parsed));
    setDraft(clamped);
    if (clamped !== value) onSave(clamped);
  };

  return (
    <Row>
      <Text>
        {text(control.labels, language)}
        {control.hints && <small>{text(control.hints, language)}</small>}
      </Text>
      {control.type === 'toggle' ? (
        <Toggle on={Boolean(value)} onChange={onSave} disabled={disabled} label={text(control.labels, language)} />
      ) : (
        <NumberInput
          type="number"
          inputMode="numeric"
          min={control.min}
          max={control.max}
          value={draft}
          disabled={disabled}
          aria-label={text(control.labels, language)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={saveNumber}
        />
      )}
    </Row>
  );
}

/**
 * @param {{ controls?: object[], when: 'live'|'prepare', instanceId: string,
 *   reached?: boolean, compact?: boolean }} props
 */
export default function ModuleControls({ controls = [], when, instanceId, reached = false, compact = false }) {
  const { t } = useTranslation();
  const { instance, setValue } = useInstanceRecord(instanceId);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);
  const shown = controls.filter((c) => c.when === when);
  if (!shown.length || !instanceId) return null;

  const save = async (control, next) => {
    setBusyKey(`${control.store}.${control.key}`);
    setError(null);
    try {
      await setValue(control.store, control.key, next);
    } catch (err) {
      setError(t('workshopOrchestrator.saveFailed', { error: err.message }));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <List $compact={compact}>
      {shown.map((control) => {
        const id = `${control.store}.${control.key}`;
        const stored = readPath(instance?.[control.store], control.key);
        const Control = control.type === 'choice' ? ChoiceRow : ControlRow;
        const locked = Boolean(control.lockOnceReached && reached);
        return (
          <React.Fragment key={id}>
            <Control
              control={control}
              value={stored ?? control.default}
              disabled={!instance || busyKey === id || locked}
              onSave={(next) => save(control, next)}
            />
            {locked && <Locked>{t('workshopOrchestrator.controlLockedReached')}</Locked>}
          </React.Fragment>
        );
      })}
      {error && <ErrorText>{error}</ErrorText>}
    </List>
  );
}
